import { useState, useCallback, useEffect, useRef } from "react";

/**
 * 🚀 sessionStorage一本化の確実な状態管理フック（製品選択保持修正版）
 * 
 * 設計方針:
 * 1. sessionStorageを唯一の永続化手段とする
 * 2. URLパラメータは使用しない（競合状態を回避）
 * 3. JWTトークンと同じ管理方式で確実性を担保
 * 4. 30分間の状態保持（タイムスタンプ管理）
 * 5. 🚀 製品選択も確実に保持する
 */
export const useSessionClientState = () => {
  const STORAGE_KEY = "clientFilters_v2";
  const EXPIRY_TIME = 30 * 60 * 1000; // 30分

  // デバウンス用のタイマー参照
  const searchDebounceRef = useRef(null);
  const persistTimerRef = useRef(null);

  /* ------------------------------------------------------------------ */
  /* 初期状態の定義                                                      */
  /* ------------------------------------------------------------------ */
  const getInitialState = useCallback(() => {
    return {
      selectedProductId: null, // 🚀 重要: nullから開始
      searchTerm: "",
      searchQuery: "",
      selectedGroupIds: [],
      activeSorting: "",
      allFilters: {
        remainingMin: "",
        remainingMax: "",
        ratioMin: "",
        ratioMax: "",
        hideNoData: false,
        includeUnregistered: true,
      },
      page: 0,
      activeFilters: 0,
      timestamp: Date.now()
    };
  }, []);

  /* ------------------------------------------------------------------ */
  /* sessionStorageからの状態復元                                        */
  /* ------------------------------------------------------------------ */
  const loadFromStorage = useCallback(() => {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      if (!stored) return getInitialState();

      const parsed = JSON.parse(stored);
      
      // タイムスタンプチェック（30分以内のみ有効）
      if (!parsed.timestamp || Date.now() - parsed.timestamp > EXPIRY_TIME) {
        sessionStorage.removeItem(STORAGE_KEY);
        return getInitialState();
      }

      // 🚀 修正: 製品IDの復元を強化
      let restoredProductId = parsed.selectedProductId;
      
      // 型安全性のチェック
      if (restoredProductId === "all") {
        restoredProductId = "all";
      } else if (typeof restoredProductId === "number" && !isNaN(restoredProductId)) {
        restoredProductId = restoredProductId;
      } else if (typeof restoredProductId === "string" && restoredProductId !== "null" && restoredProductId !== "") {
        const numericId = Number(restoredProductId);
        restoredProductId = !isNaN(numericId) ? numericId : null;
      } else {
        restoredProductId = null;
      }

      // データ整合性チェック
      return {
        selectedProductId: restoredProductId, // 🚀 修正済み
        searchTerm: parsed.searchTerm || "",
        searchQuery: parsed.searchQuery || "",
        selectedGroupIds: Array.isArray(parsed.selectedGroupIds) ? parsed.selectedGroupIds : [],
        activeSorting: parsed.activeSorting || "",
        allFilters: {
          remainingMin: parsed.allFilters?.remainingMin ?? "",
          remainingMax: parsed.allFilters?.remainingMax ?? "",
          ratioMin: parsed.allFilters?.ratioMin ?? "",
          ratioMax: parsed.allFilters?.ratioMax ?? "",
          hideNoData: Boolean(parsed.allFilters?.hideNoData),
          includeUnregistered: parsed.allFilters?.includeUnregistered !== false,
        },
        page: Math.max(0, Number(parsed.page) || 0),
        activeFilters: Number(parsed.activeFilters) || 0,
        timestamp: parsed.timestamp
      };
    } catch (error) {
      sessionStorage.removeItem(STORAGE_KEY);
      return getInitialState();
    }
  }, [getInitialState, STORAGE_KEY, EXPIRY_TIME]);

  /* ------------------------------------------------------------------ */
  /* React State                                                       */
  /* ------------------------------------------------------------------ */
  const [state, setState] = useState(getInitialState); // 🚀 修正: 初期値は常にgetInitialState

  /* ------------------------------------------------------------------ */
  /* sessionStorageへの状態保存                                          */
  /* ------------------------------------------------------------------ */
  const saveToStorage = useCallback((newState) => {
    try {
      const stateToSave = {
        ...newState,
        timestamp: Date.now()
      };
      
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(stateToSave));
    } catch (error) {
      // ストレージエラーは無視して継続（メモリ上では動作）
    }
  }, [STORAGE_KEY]);

  /* ------------------------------------------------------------------ */
  /* アクティブフィルター数の計算                                         */
  /* ------------------------------------------------------------------ */
  const calculateActiveFilters = useCallback((newState) => {
    let count = 0;
    
    // グループフィルター
    if (newState.selectedGroupIds.length > 0) count++;
    
    // データ不完全フィルター
    if (newState.allFilters.includeUnregistered === false) count++;
    
    // 製品関連フィルター（「すべて」以外の場合のみ）
    if (newState.selectedProductId !== "all") {
      const filterKeys = ['remainingMin', 'remainingMax', 'ratioMin', 'ratioMax'];
      filterKeys.forEach(key => {
        if (newState.allFilters[key] && newState.allFilters[key] !== '') {
          count++;
        }
      });
      
      if (newState.allFilters.hideNoData) count++;
    }
    
    return count;
  }, []);

  /* ------------------------------------------------------------------ */
  /* 状態更新の統一関数                                                  */
  /* ------------------------------------------------------------------ */
  const updateState = useCallback((updates) => {
    setState(prevState => {
      const newState = { ...prevState, ...updates };
      newState.activeFilters = calculateActiveFilters(newState);
      
      // 遅延保存でパフォーマンス最適化
      if (persistTimerRef.current) {
        clearTimeout(persistTimerRef.current);
      }
      persistTimerRef.current = setTimeout(() => {
        saveToStorage(newState);
      }, 100);
      
      return newState;
    });
  }, [calculateActiveFilters, saveToStorage]);

  /* ------------------------------------------------------------------ */
  /* 検索語のデバウンス処理                                              */
  /* ------------------------------------------------------------------ */
  useEffect(() => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    if (state.searchTerm !== state.searchQuery) {
      searchDebounceRef.current = setTimeout(() => {
        updateState({
          searchQuery: state.searchTerm,
          page: 0 // 検索時はページをリセット
        });
      }, 500);
    }

    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, [state.searchTerm, state.searchQuery, updateState]);

  /* ------------------------------------------------------------------ */
  /* クリーンアップ処理                                                  */
  /* ------------------------------------------------------------------ */
  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
      if (persistTimerRef.current) {
        clearTimeout(persistTimerRef.current);
      }
    };
  }, []);

  /* ------------------------------------------------------------------ */
  /* ハンドラー関数群                                                    */
  /* ------------------------------------------------------------------ */

  const handleProductSelect = useCallback((productId) => {
    const updates = { 
      selectedProductId: productId, 
      page: 0 
    };

    // 「すべて」選択時は製品関連フィルターをクリア
    if (productId === "all") {
      updates.allFilters = {
        ...state.allFilters,
        remainingMin: "",
        remainingMax: "",
        ratioMin: "",
        ratioMax: "",
        hideNoData: false,
        // includeUnregisteredは維持
      };
      updates.activeSorting = "";
    }

    updateState(updates);
  }, [state.allFilters, updateState]);

  const handleFilterApply = useCallback((filterData) => {
    updateState({
      selectedGroupIds: filterData.selectedGroups || [],
      allFilters: {
        remainingMin: filterData.remainingMin || "",
        remainingMax: filterData.remainingMax || "",
        ratioMin: filterData.ratioMin || "",
        ratioMax: filterData.ratioMax || "",
        hideNoData: filterData.hideNoData || false,
        includeUnregistered: filterData.includeUnregistered !== false,
      },
      page: 0
    });
  }, [updateState]);

  const handleSortApply = useCallback((option) => {
    // 「すべて」選択時はソート無効
    if (state.selectedProductId === "all") return;
    
    updateState({
      activeSorting: option,
      page: 0
    });
  }, [state.selectedProductId, updateState]);

  const handlePageChange = useCallback((newPage) => {
    updateState({ page: newPage });
  }, [updateState]);

  const clearState = useCallback(() => {
    const clearedState = {
      selectedGroupIds: [],
      allFilters: {
        remainingMin: "",
        remainingMax: "",
        ratioMin: "",
        ratioMax: "",
        hideNoData: false,
        includeUnregistered: true,
      },
      activeSorting: "",
      searchTerm: "",
      searchQuery: "",
      page: 0
      // 🚀 重要: selectedProductIdは維持（製品選択はクリアしない）
    };
    updateState(clearedState);
  }, [updateState]);

  /* ------------------------------------------------------------------ */
  /* セッター関数群（UI即座更新用）                                      */
  /* ------------------------------------------------------------------ */

  const setSearchTerm = useCallback((term) => {
    setState(prev => ({ ...prev, searchTerm: term }));
  }, []);

  const setSelectedGroupIds = useCallback((ids) => {
    updateState({ selectedGroupIds: ids });
  }, [updateState]);

  const setActiveSorting = useCallback((sort) => {
    updateState({ activeSorting: sort });
  }, [updateState]);

  const setAllFilters = useCallback((filters) => {
    updateState({ allFilters: filters });
  }, [updateState]);

  const setActiveFilters = useCallback((count) => {
    updateState({ activeFilters: count });
  }, [updateState]);

  const setSelectedProductId = useCallback((id) => {
    updateState({ selectedProductId: id });
  }, [updateState]);

  /* ------------------------------------------------------------------ */
  /* 🚀 修正: 状態復元の強化                                           */
  /* ------------------------------------------------------------------ */

  const restoreState = useCallback(() => {
    const restoredState = loadFromStorage();
    
    // 🚀 重要: 復元した状態を即座にReactステートに反映
    setState(restoredState);
    
    return { 
      productId: restoredState.selectedProductId, 
      restored: true, 
      hasStoredState: !!sessionStorage.getItem(STORAGE_KEY)
    };
  }, [loadFromStorage, STORAGE_KEY]);

  const persistState = useCallback((updates = {}) => {
    if (Object.keys(updates).length > 0) {
      updateState(updates);
    } else {
      saveToStorage(state);
    }
  }, [updateState, saveToStorage, state]);

  /* ------------------------------------------------------------------ */
  /* 返却値                                                              */
  /* ------------------------------------------------------------------ */
  return {
    // 状態
    searchTerm: state.searchTerm,
    searchQuery: state.searchQuery,
    selectedGroupIds: state.selectedGroupIds,
    activeSorting: state.activeSorting,
    allFilters: state.allFilters,
    page: state.page,
    activeFilters: state.activeFilters,
    selectedProductId: state.selectedProductId,

    // セッター
    setSearchTerm,
    setSelectedGroupIds,
    setActiveSorting,
    setAllFilters,
    setActiveFilters,
    setSelectedProductId,

    // アクション
    handleProductSelect,
    handleFilterApply,
    handleSortApply,
    handlePageChange,

    // 互換性
    restoreState,
    persistState,
    clearState,
  };
};